import catalogJson from "./audio-models.json";

export type AudioVoiceOption = { id: string; label: string; previewUrl?: string };
export type AudioModelKind = "voice" | "sfx" | "music";
export type AudioBilling = "per_1k" | "per_generation";

export type AudioModelOption = {
  id: string;
  label: string;
  kind?: AudioModelKind;
  billing?: AudioBilling;
  provider: "poyo";
  poyoModel: string;
  costSettingId: string;
  creditsPer1kChars: number;
  cogsUsdPer1kChars: number;
  minCreditsPer1kChars: number;
  creditsPerGeneration?: number;
  cogsUsdPerGeneration?: number;
  minCreditsPerGeneration?: number;
  maxChars: number;
  defaultVoice: string;
  defaultLanguage: string;
  defaultMv?: string;
  requiresSourceAudio?: boolean;
  voices: AudioVoiceOption[];
};

export const AUDIO_MODEL_CATALOG: AudioModelOption[] = catalogJson as AudioModelOption[];

export function getAudioModelById(id: string): AudioModelOption | undefined {
  return AUDIO_MODEL_CATALOG.find((m) => m.id === id || m.poyoModel === id);
}

export function getSoundModel(id?: string): AudioModelOption | undefined {
  const found = getAudioModelById(String(id || ""));
  if (!found || !isSoundKind(found.kind)) return undefined;
  return found;
}

export function isSoundKind(kind?: string): boolean {
  return kind === "sfx" || kind === "music";
}

export function audioModelsByKind(kind: AudioModelKind): AudioModelOption[] {
  return AUDIO_MODEL_CATALOG.filter((m) => (m.kind || "voice") === kind);
}

export function defaultAudioModel(kind: AudioModelKind = "voice"): AudioModelOption {
  return audioModelsByKind(kind)[0] || AUDIO_MODEL_CATALOG[0];
}

export function pickAudioModel(id?: string, kind?: AudioModelKind): AudioModelOption {
  const found = getAudioModelById(String(id || ""));
  if (!found) return defaultAudioModel(kind);
  const foundKind = found.kind || "voice";
  if (!kind) return found;
  if (kind === "voice") return foundKind === "voice" ? found : defaultAudioModel("voice");
  return foundKind === kind ? found : defaultAudioModel(kind);
}

export function isPerGenerationAudio(model: AudioModelOption): boolean {
  return (model.billing || "per_1k") === "per_generation" || isSoundKind(model.kind);
}

export function billableCharUnits(text: string, maxChars: number): { chars: number; units: number } {
  const chars = Math.max(1, Math.min(maxChars, Array.from(text.trim()).length));
  return { chars, units: Math.max(1, Math.ceil(chars / 1000)) };
}

export function quoteAudioCredits(modelId: string, text: string): number {
  const model = pickAudioModel(modelId);
  if (isPerGenerationAudio(model)) {
    return model.creditsPerGeneration ?? model.creditsPer1kChars;
  }
  const { units } = billableCharUnits(text, model.maxChars);
  return model.creditsPer1kChars * units;
}
